import {
  SyntheticEvent,
  useRef,
  useEffect,
  useState,
  useCallback,
} from 'react';

import ReactCrop, { Crop, PercentCrop, PixelCrop } from 'react-image-crop';

import { useContainerSize } from '../hooks/useContainerSize';
import { Box } from '../Box';

import 'react-image-crop/dist/ReactCrop.css';

import { canvasPreview } from './canvasPreview';
import { useCrop } from './useCrop';
import { useImageLoader } from './useImageLoader';
import { calculateInnerBoxDimensions } from './calculateInnerBoxDimensions';
import { useStyles } from './ImageCropper.styles';

/**
 * Свойства компонента {@link ImageCropper}
 */
interface ImageCropperProps {
  /**
   * Blob-объект, содержащий URL изображения в виде текста (JSON).
   */
  blob: Blob | null;

  /**
   * Соотношение сторон для области кадрирования.
   *
   * @defaultValue `1.78`
   */
  sizeAspect?: number;

  /**
   * Функция-колбэк, вызываемый после завершения кадрирования выбранной области изображения.
   */
  onCropComplete?: (croppedBlob: Blob) => void;
}

/**
 * Компонент для кадрирования изображения с использованием библиотеки react-easy-crop.
 *
 * @public
 * @param blob - Blob-объект, содержащий URL изображения в виде текста (JSON).
 * @param sizeAspect - Соотношение сторон для области кадрирования.
 * @param onCropComplete - Функция-колбэк, вызываемый после завершения кадрирования выбранной области изображения.
 * @returns  Компонент для кадрирования изображения.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link ImageCropperProps}
 */
export const ImageCropper = ({
  blob,
  sizeAspect = 1.78,
  onCropComplete,
}: ImageCropperProps) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const containerSize = useContainerSize(containerRef);

  const { imageUrl, imageSize } = useImageLoader(blob);

  const { crop, completedCrop, setCrop, setCompletedCrop, handleImageLoad } =
    useCrop();

  const { classes } = useStyles({ completedCrop });

  const toBlob = useCallback(
    (canvas: HTMLCanvasElement): Promise<Blob | null> => {
      return new Promise((resolve) => {
        canvas.toBlob(resolve);
      });
    },
    [],
  );

  const handleCropComplete = useCallback(
    async (cropElement: PixelCrop, percentCrop: PercentCrop) => {
      if (
        !imgRef.current ||
        !previewCanvasRef.current ||
        !percentCrop ||
        !percentCrop.width ||
        !percentCrop.height
      ) {
        return;
      }

      const { naturalWidth, naturalHeight } = imgRef.current;

      // Конвертируем процентный crop напрямую в натуральные пиксели
      const naturalCrop: PixelCrop = {
        unit: 'px',
        x: (percentCrop.x / 100) * naturalWidth,
        y: (percentCrop.y / 100) * naturalHeight,
        width: (percentCrop.width / 100) * naturalWidth,
        height: (percentCrop.height / 100) * naturalHeight,
      };

      setCompletedCrop(naturalCrop);

      await canvasPreview(
        imgRef.current,
        previewCanvasRef.current,
        naturalCrop,
      );

      const newBlob = await toBlob(previewCanvasRef.current);

      if (newBlob && onCropComplete) {
        onCropComplete(newBlob);
      }
    },
    [onCropComplete, setCompletedCrop, toBlob],
  );

  const [initialCropApplied, setInitialCropApplied] = useState(false);

  useEffect(() => {
    if (
      crop &&
      !initialCropApplied &&
      imgRef.current &&
      previewCanvasRef.current
    ) {
      const { naturalWidth, naturalHeight } = imgRef.current;

      const naturalCrop: PixelCrop = {
        unit: 'px',
        x: (crop.x / 100) * naturalWidth,
        y: (crop.y / 100) * naturalHeight,
        width: (crop.width / 100) * naturalWidth,
        height: (crop.height / 100) * naturalHeight,
      };

      setCompletedCrop(naturalCrop);

      canvasPreview(imgRef.current, previewCanvasRef.current, naturalCrop).then(
        () => {
          if (!previewCanvasRef.current) return;

          toBlob(previewCanvasRef.current).then((newBlob) => {
            if (newBlob && onCropComplete) {
              onCropComplete(newBlob);
            }
          });
        },
      );

      setInitialCropApplied(true);
    }
  }, [crop, initialCropApplied, onCropComplete, setCompletedCrop, toBlob]);

  const innerBoxDimensions = calculateInnerBoxDimensions(
    imageSize.width,
    imageSize.height,
    containerSize.width,
    containerSize.height,
  );

  const isImageLoaded = imageUrl && imageSize.width && imageSize.height;

  const handleCropChange = (newCrop: Crop, percentCrop: PercentCrop) => {
    setCrop(percentCrop);
  };

  const getMaxCrop = (
    imageWidth: number,
    imageHeight: number,
    aspect: number,
  ): PercentCrop => {
    const imageAspect = imageWidth / imageHeight;
    if (imageAspect > aspect) {
      const cropHeight = 100;
      const cropWidth = ((aspect * imageHeight) / imageWidth) * 100;

      return {
        unit: '%',
        x: (100 - cropWidth) / 2,
        y: 0,
        width: cropWidth,
        height: cropHeight,
      };
    } else {
      const cropWidth = 100;
      const cropHeight = (imageWidth / aspect / imageHeight) * 100;
      return {
        unit: '%',
        x: 0,
        y: (100 - cropHeight) / 2,
        width: cropWidth,
        height: cropHeight,
      };
    }
  };

  const handleLoad = (e: SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = e.currentTarget;

    handleImageLoad(naturalWidth, naturalHeight);

    const maxCrop = getMaxCrop(naturalWidth, naturalHeight, sizeAspect);

    setCrop(maxCrop);
    setInitialCropApplied(false);
  };

  useEffect(() => {
    if (crop && imageSize.width > 0 && imageSize.height > 0) {
      const maxCrop = getMaxCrop(imageSize.width, imageSize.height, sizeAspect);
      setCrop(maxCrop);
      setInitialCropApplied(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sizeAspect]);

  if (!blob) return null;

  return (
    <Box
      flex="1"
      minHeight="100%"
      width="100%"
      bgcolor="bg.greyscale.primary"
      ref={containerRef}
    >
      {isImageLoaded && (
        <Box
          width={innerBoxDimensions.width}
          height={innerBoxDimensions.height}
          className={classes.cropContainer}
          testId="crop-container"
        >
          <ReactCrop
            crop={crop}
            onChange={handleCropChange}
            onComplete={handleCropComplete}
            aspect={sizeAspect}
            className={classes.ReactCrop}
          >
            <img
              ref={imgRef}
              alt="Crop me"
              src={imageUrl}
              onLoad={handleLoad}
              className={classes.cropImage}
              data-testid="crop-image"
            />
          </ReactCrop>
        </Box>
      )}
      <canvas
        ref={previewCanvasRef}
        className={classes.canvasPreview}
        data-testid="crop-canvas"
      />
    </Box>
  );
};
